import { useSyncExternalStore } from 'react';
import {
  getFruitNinjaRemoteUiSnapshot,
  subscribeFruitNinjaRemoteUi,
} from '../../state/fruitNinjaRemoteUiStore.ts';
import { FRUIT_NINJA_REMOTE_DOM } from './fruitNinjaRemoteDomIds.ts';
import { useI18n } from 'shared/lib/i18n.tsx';

export function FruitNinjaRemoteGameOver() {
  const d = FRUIT_NINJA_REMOTE_DOM;
  const { t } = useI18n();
  const s = useSyncExternalStore(
    subscribeFruitNinjaRemoteUi,
    getFruitNinjaRemoteUiSnapshot,
    getFruitNinjaRemoteUiSnapshot,
  );

  const isDuo = s.split && s.player1Score !== null && s.player2Score !== null;

  // One player's lives ran out but the other is still playing (split ARCADE).
  const p1DoneOnly = s.player1GameOver && !s.player2GameOver;
  const p2DoneOnly = s.player2GameOver && !s.player1GameOver;
  const partialGameOver = isDuo && (p1DoneOnly || p2DoneOnly);

  let winner: 'p1' | 'p2' | 'tie' | null = null;
  if (isDuo && !partialGameOver) {
    if (s.player1Score! > s.player2Score!) winner = 'p1';
    else if (s.player2Score! > s.player1Score!) winner = 'p2';
    else winner = 'tie';
  }

  function panelClass(player: 'p1' | 'p2'): string {
    if (winner === 'tie') return 'fn-go__panel fn-go__panel--tie';
    if (winner === player) return 'fn-go__panel fn-go__panel--winner';
    return 'fn-go__panel fn-go__panel--loser';
  }

  // Partial game over: one player done, other still playing — shown as a small overlay,
  // not the full-screen #gameover panel so the canvas remains visible.
  if (partialGameOver) {
    const donePlayer = p1DoneOnly ? 'P1' : 'P2';
    const doneScore = p1DoneOnly ? s.player1Score : s.player2Score;
    const side = p1DoneOnly ? 'left' : 'right';
    return (
      <div className={`fn-go__partial fn-go__partial--${side}`}>
        <div className="fn-go__partial-name">{donePlayer}</div>
        <div className="fn-go__partial-score">{doneScore}</div>
        <div className="fn-go__game-over-label">{t('game.game_over')}</div>
      </div>
    );
  }

  return (
    <div id={d.gameOverRoot} className="fruit-remote-gameover" hidden={!s.gameOverVisible}>
      {isDuo ? (
        <div className="fn-go__duo">
          <div className={panelClass('p1')}>
            {winner === 'p1' && <div className="fn-go__crown" aria-hidden>🏆</div>}
            <div className="fn-go__player-name">P1</div>
            <div className="fn-go__player-score">{s.player1Score}</div>
            {winner === 'p1' && <div className="fn-go__winner-label">{t('game.winner')}</div>}
            {winner === 'tie' && <div className="fn-go__tie-label">{t('game.tie')}</div>}
          </div>

          <div className={panelClass('p2')}>
            {winner === 'p2' && <div className="fn-go__crown" aria-hidden>🏆</div>}
            <div className="fn-go__player-name">P2</div>
            <div className="fn-go__player-score">{s.player2Score}</div>
            {winner === 'p2' && <div className="fn-go__winner-label">{t('game.winner')}</div>}
            {winner === 'tie' && <div className="fn-go__tie-label">{t('game.tie')}</div>}
          </div>
        </div>
      ) : (
        <>
          <h2 className="fruit-remote-gameover__title">{t('game.game_over')}</h2>
          <div className="fruit-remote-gameover__score">{t('game.score')} {s.gameOverScore}</div>
        </>
      )}
    </div>
  );
}
